import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  AmountCell,
  FinancialStatementStyles,
  NodeNameCell,
  PresetChips,
  amount,
  nodeKey,
  periodPresets,
  printStatement,
} from "./FinancialStatementShared";

const TITLE = "Cash Flow Statement";

const ACTIVITY_NET = {
  operating: "Net Cash from Operating Activities",
  investing: "Net Cash from Investing Activities",
  financing: "Net Cash from Financing Activities",
};

// A statement line as an expandable node whose children are the accounts
// behind it.
const lineNode = (line) => ({
  key: `cf-${line.key}`,
  name: line.label,
  inflow: line.inflow,
  outflow: line.outflow,
  net: line.net,
  children: line.accounts.map((account) => ({
    ...account,
    children: [],
  })),
});

const hasMovement = (node) =>
  Math.abs(num(node.inflow)) >= 0.005 || Math.abs(num(node.outflow)) >= 0.005;

const buildRows = (data, expanded, hideZero) => {
  const rows = [];
  const cashRows = (key, label, block) => {
    rows.push({ type: "section", key: `sec-${key}`, label });
    block.accounts.forEach((account) =>
      rows.push({
        type: "line",
        key: `${key}-${account.id}`,
        depth: 1,
        // No ledger link: these are parent accounts, whose own ledger
        // shows only postings made directly on them.
        node: { code: account.code, name: account.name, children: [] },
        hasChildren: false,
        net: account.amount,
      }),
    );
  };

  cashRows("opening", "Opening Cash & Cash Equivalents", data.opening);
  rows.push({
    type: "subtotal",
    key: "opening-total",
    label: "Total Opening Cash",
    net: data.opening.total,
  });

  data.activities.forEach((activity) => {
    rows.push({ type: "section", key: `sec-${activity.key}`, label: activity.label });
    activity.lines
      .map(lineNode)
      .filter((node) => !hideZero || hasMovement(node))
      .forEach((node) => {
        const open = !!expanded[nodeKey(node)];
        rows.push({
          type: "line",
          key: node.key,
          depth: 1,
          node,
          hasChildren: node.children.length > 0,
          expanded: open,
          inflow: node.inflow,
          outflow: node.outflow,
          net: node.net,
        });
        if (open) {
          node.children.forEach((account) =>
            rows.push({
              type: "account",
              key: `${node.key}-${account.id}`,
              depth: 2,
              node: account,
              hasChildren: false,
              inflow: account.inflow,
              outflow: account.outflow,
              net: account.net,
            }),
          );
        }
      });
    rows.push({
      type: "subtotal",
      key: `net-${activity.key}`,
      label: ACTIVITY_NET[activity.key] || `Net ${activity.label}`,
      inflow: activity.inflow,
      outflow: activity.outflow,
      net: activity.net,
    });
  });

  rows.push({
    type: "total",
    key: "net-change",
    label: "Net Increase / (Decrease) in Cash",
    inflow: data.inflow,
    outflow: data.outflow,
    net: data.net_change,
  });

  cashRows("closing", "Closing Cash & Cash Equivalents", data.closing);
  rows.push({
    type: "grand",
    key: "closing-total",
    label: "Total Closing Cash",
    net: data.closing.total,
  });

  return rows;
};

// In and out are always positive; blank rather than a dash when there is no
// column value for the row.
const flow = (value) =>
  value === undefined || value === null ? "" : amount(value);

const CashFlowStatement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, thisMonthFrom, thisMonthTo] = periodPresets()[0];
  const [inputs, setInputs] = useState({
    from: searchParams.get("from") || thisMonthFrom,
    to: searchParams.get("to") || thisMonthTo,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [hideZero, setHideZero] = useState(false);

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);
    const query = { from: params.from, to: params.to };
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/cash-flow-statement", { params: query });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the statement. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const rows = useMemo(
    () => (data ? buildRows(data, expanded, hideZero) : []),
    [data, expanded, hideZero],
  );

  const expandable = useMemo(
    () =>
      data
        ? data.activities
            .flatMap((activity) => activity.lines)
            .filter((line) => line.accounts.length)
            .map((line) => `line-cf-${line.key}`)
        : [],
    [data],
  );

  const allExpanded =
    expandable.length > 0 && expandable.every((key) => expanded[key]);

  const toggleAll = () =>
    setExpanded(
      allExpanded
        ? {}
        : expandable.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

  const toggle = (node) => {
    const key = nodeKey(node);
    setExpanded((values) => ({ ...values, [key]: !values[key] }));
  };

  const periodLabel = data
    ? `${formatDate(data.from)} – ${formatDate(data.to)}`
    : "";

  const cashLabel = data
    ? data.cash_accounts.map((a) => `${a.code} - ${a.name}`).join(", ")
    : "";

  const reconciled = data && Math.abs(num(data.difference)) < 0.005;

  const renderRow = (row) => {
    if (row.type === "section") {
      return (
        <tr key={row.key} className="fs-section">
          <td colSpan={5}>{row.label}</td>
        </tr>
      );
    }
    const isNode = row.type === "line" || row.type === "account";
    return (
      <tr key={row.key} className={`fs-${row.type}`}>
        {isNode ? (
          <NodeNameCell row={row} onToggle={toggle} />
        ) : (
          <td>{row.label}</td>
        )}
        <td className="fs-code">{isNode ? row.node.code : ""}</td>
        <td className="coa-num">{flow(row.inflow)}</td>
        <td className="coa-num">{flow(row.outflow)}</td>
        <AmountCell value={row.net} />
      </tr>
    );
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Particulars</th><th>Code</th>
      <th class="num">Cash In</th><th class="num">Cash Out</th><th class="num">Net</th>`;
    const bodyHtml = rows
      .map((row) => {
        if (row.type === "section") {
          return `<tr class="section"><td colspan="5">${row.label}</td></tr>`;
        }
        const isNode = row.type === "line" || row.type === "account";
        const pad = isNode ? 16 + row.depth * 16 : 8;
        return `
          <tr class="${row.type}">
            <td style="padding-left:${pad}px">${isNode ? row.node.name : row.label}</td>
            <td class="code">${isNode && row.node.code ? row.node.code : ""}</td>
            <td class="num">${flow(row.inflow)}</td>
            <td class="num">${flow(row.outflow)}</td>
            <td class="num">${amount(row.net)}</td>
          </tr>`;
      })
      .join("");

    printStatement({
      title: TITLE,
      subtitle: `For the period ${periodLabel} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: reconciled
        ? `Direct method: movements on ${cashLabel}, classed by the other side of each voucher. Opening cash plus net cash flow equals closing cash.`
        : `Opening cash plus net cash flow differs from closing cash by ${amount(data.difference)}.`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              For the period{" "}
              {data
                ? periodLabel
                : `${formatDate(inputs.from)} – ${formatDate(inputs.to)}`}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Period
              </span>
              {data && (
                <span className="coa-head-note">
                  Movements on {cashLabel} · {data.vouchers} voucher
                  {data.vouchers === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-6">
                  <label>Period</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="from"
                      value={inputs.from}
                      onChange={handleChange}
                      max={inputs.to}
                      aria-label="From"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="to"
                      value={inputs.to}
                      onChange={handleChange}
                      min={inputs.from}
                      aria-label="To"
                    />
                  </div>
                  <PresetChips
                    presets={periodPresets()}
                    isActive={([, from, to]) =>
                      inputs.from === from && inputs.to === to
                    }
                    onPick={([, from, to]) => apply({ from, to })}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.from || !inputs.to}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Opening Cash",
                "fa-flag-o",
                amount(data.opening.total),
                `As at ${formatDate(data.from)}`,
              )}
              {stat(
                "Net Cash Flow",
                "fa-exchange",
                amount(data.net_change),
                `In ${amount(data.inflow)} · out ${amount(data.outflow)}`,
                num(data.net_change) < 0 ? "is-alert" : "is-ok",
              )}
              {stat(
                "Closing Cash",
                "fa-university",
                amount(data.closing.total),
                `As at ${formatDate(data.to)}`,
              )}
              {stat(
                "Reconciliation",
                "fa-check-square-o",
                reconciled ? "Reconciled" : "Difference",
                reconciled
                  ? "Opening + net flow = closing"
                  : `Out by ${amount(data.difference)}`,
                reconciled ? "is-ok" : "is-alert",
              )}
            </div>
          )}

          {data && !reconciled && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                Opening cash plus net cash flow differs from closing cash by{" "}
                <strong>{amount(data.difference)}</strong>. A voucher that
                moves cash does not balance; check the vouchers in this period.
              </span>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-file-text-o me-2"></i>
                {TITLE}
                <span className="coa-head-note mg-l-10">Amounts in Taka</span>
              </span>
              {data && (
                <span className="d-flex align-items-center gap-3 coa-no-print">
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      checked={hideZero}
                      onChange={(e) => setHideZero(e.target.checked)}
                    />
                    Hide lines with no movement
                  </label>
                  {expandable.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={toggleAll}
                    >
                      <i
                        className={`fa fa-${allExpanded ? "compress" : "expand"} mg-r-6`}
                      ></i>
                      {allExpanded ? "Collapse all" : "Expand all"}
                    </button>
                  )}
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The statement could not be loaded.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="fs-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Code</th>
                      <th className="coa-num">Cash In</th>
                      <th className="coa-num">Cash Out</th>
                      <th className="coa-num">Net</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashFlowStatement;
